import { useNavigate } from 'react-router-dom';
import { Clock, MapPin, AlertTriangle } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  useMyClosingRodeos,
  dashboardDataKeys,
  type ClosingRodeo,
} from '@/api/extensions/useDashboardData';
import { CountdownTimer } from '@/components/shared_reusable_components/CountdownTimer';
import { formatInTimezone } from '@/utils/timezone';

/**
 * Prominent dashboard-top banner for rodeos whose entry-close time is
 * within the next 3 days. Hero slot is the soonest-closing rodeo with a
 * live countdown + "Enter Now" CTA that routes to the rodeo detail page
 * (where the user can browse and enter individual events). When more
 * than one rodeo is in the window, the remainder are listed underneath
 * the hero with their own compact countdowns so the user can pick.
 *
 * Self-hides when the query is empty — no need to wrap mounts in
 * conditional logic upstream.
 */
export function EntryCloseBanner() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: rodeos, isLoading } = useMyClosingRodeos();

  if (isLoading || !rodeos || rodeos.length === 0) return null;

  const [hero, ...rest] = rodeos;

  const handleExpire = () => {
    // A rodeo crossed its close-time mid-session. Drop it from the list
    // by re-fetching; the server-side filter excludes past close times.
    queryClient.invalidateQueries({ queryKey: dashboardDataKeys.myClosingRodeos() });
  };

  return (
    <div
      role="region"
      aria-label="Entries closing soon"
      className="rounded-xl border-2 border-amber-300 dark:border-amber-700/60 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/40 dark:to-orange-950/40 overflow-hidden shadow-sm"
    >
      {/* Hero row */}
      <HeroRow rodeo={hero} onNavigate={navigate} onExpire={handleExpire} />

      {rest.length > 0 && (
        <div className="border-t border-amber-300 dark:border-amber-700/60 bg-amber-100/40 dark:bg-amber-950/30 px-4 py-3">
          <div className="text-xs font-semibold uppercase tracking-wider text-amber-900 dark:text-amber-200 mb-2">
            Also closing soon
          </div>
          <ul className="space-y-2">
            {rest.map((r) => (
              <li key={r.id}>
                <SecondaryRow
                  rodeo={r}
                  onNavigate={navigate}
                  onExpire={handleExpire}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

interface RowProps {
  rodeo: ClosingRodeo;
  onNavigate: (to: string) => void;
  onExpire: () => void;
}

function HeroRow({ rodeo, onNavigate, onExpire }: RowProps) {
  const tz = rodeo.timezone || 'America/Los_Angeles';
  const closesAbs = formatInTimezone(
    rodeo.entry_close_time,
    tz,
    { dateStyle: 'medium', timeStyle: 'short' },
    true,
  );

  return (
    <div className="p-4 sm:p-5">
      <div className="flex items-start justify-between gap-4 flex-wrap sm:flex-nowrap">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0" />
            <span className="text-xs font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
              Entries closing soon
            </span>
            {rodeo.rodeo_or_jackpot === 'jackpot' && (
              <Badge variant="outline" className="text-[10px] uppercase">
                Jackpot
              </Badge>
            )}
          </div>
          <h2 className="text-lg sm:text-xl font-bold tracking-tight text-foreground truncate">
            {rodeo.name || 'Unnamed rodeo'}
          </h2>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {rodeo.location && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {rodeo.location}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Closes {closesAbs}
            </span>
          </div>
          <CountdownTimer
            target={rodeo.entry_close_time}
            variant="full"
            onExpire={onExpire}
            className="block font-mono text-2xl sm:text-3xl font-bold tabular-nums text-amber-700 dark:text-amber-200"
          />
        </div>
        <div className="shrink-0">
          <Button
            size="lg"
            className="bg-amber-600 hover:bg-amber-700 text-white shadow-md"
            onClick={() => onNavigate(`/rodeos/${rodeo.id}`)}
          >
            Enter Now
          </Button>
        </div>
      </div>
    </div>
  );
}

function SecondaryRow({ rodeo, onNavigate, onExpire }: RowProps) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md bg-white/50 dark:bg-black/20 px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm truncate">
            {rodeo.name || 'Unnamed rodeo'}
          </span>
          {rodeo.rodeo_or_jackpot === 'jackpot' && (
            <Badge variant="outline" className="text-[10px] uppercase">
              Jackpot
            </Badge>
          )}
        </div>
      </div>
      <CountdownTimer
        target={rodeo.entry_close_time}
        variant="compact"
        onExpire={onExpire}
        className="font-mono text-sm font-semibold tabular-nums text-amber-700 dark:text-amber-300 shrink-0"
      />
      <Button
        size="sm"
        variant="outline"
        className="shrink-0"
        onClick={() => onNavigate(`/rodeos/${rodeo.id}`)}
      >
        Enter
      </Button>
    </div>
  );
}
